import React from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Button,
  useDisclosure,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiList as _FiList, FiFileText as _FiFileText } from "react-icons/fi";

const FiList = _FiList as unknown as React.ComponentType<any>;
const FiFileText = _FiFileText as unknown as React.ComponentType<any>;

// ✅ User Type Definition
type Task = { id: string; name: string };
type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  assignedProjects?: string[];
  assignedTasks?: (string | Task)[];
  organizationId: string;
};

// ✅ Component Props Interface
interface AssignedTasksModalProps {
  user: User;
  color: string;
  bg: string;
}

const AssignedTasksModal: React.FC<AssignedTasksModalProps> = ({
  user,
  color,
  bg,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure(); // Chakra UI Hook
  const modalBg = useColorModeValue("white", "gray.800");
  const gold = "#B59143";
  const goldSoft = "#FBD282";
  const iconColor = useColorModeValue("#6B7280", "#C3C9D3");
  const hoverBg = useColorModeValue("#FFF7E6", "#2A3441");

  // ✅ Filter valid tasks
  const validTasks = (user.assignedTasks || []).filter(
    (task): task is Task =>
      typeof task === "object" && "id" in task && "name" in task
  );

  return (
    <>
      {/* Button to Open Modal */}
      <IconButton
        aria-label="View Assigned Tasks"
        icon={<FiList />}
        variant="ghost"
        size="sm"
        borderRadius="full"
        color={iconColor}
        _hover={{ color: gold, bg: "transparent", transform: "translateY(-1px)" }}
        transition="all 0.15s ease"
        onClick={onOpen} // Open modal on click
      />

      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent bg={modalBg} borderRadius="lg" boxShadow="2xl">
          <ModalHeader fontWeight="bold" color={color}>
            Assigned Tasks
          </ModalHeader>
          <ModalCloseButton color={color} />

          <ModalBody color={color} maxHeight="350px" overflowY="auto" px={4}>
            {validTasks.length > 0 ? (
              <VStack align="stretch" spacing={3}>
                {validTasks.map((task) => (
                  <HStack

                    key={task.id}
                    p={3}
                    borderRadius="md"
                    bg="gray.50"
                    _dark={{ bg: "gray.700" }}
                    _hover={{ bg: hoverBg, cursor: "pointer", color: gold }}
                    boxShadow="md"
                    spacing={3}
                  >
                    {/* ✅ Fixed Icon on Left */}
                    <FiFileText size={18} color={gold} />

                    {/* ✅ Task Name with Horizontal Scrolling */}
                    <Box
                      flex="1"
                      overflowX="auto"
                      whiteSpace="nowrap"
                      sx={{
                        "&::-webkit-scrollbar": {
                          height: "4px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: "#CBD5E0",
                          borderRadius: "10px",
                        },
                      }}
                    >
                      <Text fontWeight="medium" fontSize="sm">
                        {task.name}
                      </Text>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            ) : (
              <Text fontSize="sm" color="gray.500" textAlign="center" p={4}>
                No tasks assigned.
              </Text>
            )}
          </ModalBody>

          <Box textAlign="center" py={4}>
            <Button variant="gold" size="sm" onClick={onClose}>
              Close
            </Button>
          </Box>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AssignedTasksModal;
